import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, FileText, ShieldAlert, ShieldCheck, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { UploadDocumentoModal } from "@/components/UploadDocumentoModal";
import { DocumentoPreviewModal } from "@/components/DocumentoPreviewModal";
import { formatDate } from "@/components/DetailLayout";
import { useRolesEfectivos } from "@/lib/sesion";

// ============================================================================
// Panel genérico de documentos de la emisión (incidencia 2026-07-13).
// Reutilizable por cualquier servicio: los tipos permitidos vienen de la
// configuración del catálogo (kernel.servicio.tipos_documento_final) vía el
// detalle de la solicitud — un servicio nuevo solo configura sus tipos.
// La carga se habilita únicamente con el flujo concluido en ACEPTADA (roles
// de emisión); la consulta es para todos los que ven la solicitud.
// ============================================================================

interface DocResumen {
  documentoId: string;
  tipoCodigo: string;
  tipoNombre: string;
  nombreArchivo: string;
  antivirusEstado: string;
  fechaCarga: string;
}

const ROLES_SUBIR = new Set(["ADMIN_SICEN", "RESPONSABLE_TELECOM", "COORDINADOR_TECNICO"]);


export function EmisionDocumentosPanel({
  solicitudId,
  tipos,
  tipoPrincipal,
  textoBoton,
  tituloLista = "Documentos de la emisión",
}: {
  solicitudId: string;
  /** Códigos de tipo de documento permitidos (tipos_documento_final del servicio). */
  tipos: string[];
  /** Tipo que se distingue como «Principal» en el listado (opcional). */
  tipoPrincipal?: string;
  textoBoton: string;
  tituloLista?: string;
}) {
  const qc = useQueryClient();
  const puedeSubir = useRolesEfectivos().some((r) => ROLES_SUBIR.has(r));
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);

  const qEstado = useQuery<{ cerrada: boolean; estado: { codigo: string } }>({
    queryKey: ["solicitud-estado", solicitudId],
    queryFn: async () =>
      (await api.get<{ cerrada: boolean; estado: { codigo: string } }>(
        `/api/sector/telecom/solicitudes/${solicitudId}/estado`,
      )).data,
  });
  const emitida = !!qEstado.data?.cerrada && qEstado.data.estado.codigo === "ACEPTADA";

  const qDocs = useQuery<DocResumen[]>({
    queryKey: ["emision-docs", solicitudId],
    queryFn: async () =>
      (await api.get<DocResumen[]>(
        `/api/documentos/contexto?contextoTipo=SOLICITUD_TELECOM&contextoId=${solicitudId}`,
      )).data,
  });
  const docs = (qDocs.data ?? []).filter((d) => tipos.includes(d.tipoCodigo));

  if (!emitida && docs.length === 0) return null;

  return (
    <div className="space-y-3">
      {emitida && puedeSubir && (
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover"
        >
          <Upload className="h-4 w-4" /> {textoBoton}
        </button>
      )}
      <div className="rounded border">
        <div className="border-b bg-surface-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
          {tituloLista} ({docs.length})
        </div>
        {qDocs.isLoading ? (
          <div className="p-3 text-sm text-fg-subtle">Cargando…</div>
        ) : docs.length === 0 ? (
          <div className="p-3 text-sm text-fg-subtle">Aún no se han subido documentos.</div>
        ) : (
          <ul className="divide-y">
            {docs.map((doc) => (
              <li key={doc.documentoId} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-fg-faint" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-mono text-xs text-fg-muted" title={doc.nombreArchivo}>
                        {doc.nombreArchivo}
                      </span>
                      {tipoPrincipal && doc.tipoCodigo === tipoPrincipal ? (
                        <span className="rounded bg-estado-ok-bg px-1.5 py-0.5 text-[10px] font-semibold text-estado-ok">
                          Principal
                        </span>
                      ) : (
                        <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-fg-subtle">
                          {doc.tipoNombre}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-fg-subtle">
                      <span>Cargado {formatDate(doc.fechaCarga, true)}</span>
                      {doc.antivirusEstado === "LIMPIO" ? (
                        <span className="inline-flex items-center gap-1 text-estado-ok"><ShieldCheck className="h-3 w-3" /> Limpio</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-estado-warn"><ShieldAlert className="h-3 w-3" /> {doc.antivirusEstado}</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewDocId(doc.documentoId)}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded border border-edge px-2 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
                >
                  <Eye className="h-3 w-3" /> Ver / descargar
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {uploadOpen && (
        <UploadDocumentoModal
          open={uploadOpen}
          title={textoBoton}
          contextoTipo="SOLICITUD_TELECOM"
          contextoId={solicitudId}
          filterTipos={(t) => tipos.includes(t.codigo)}
          onClose={() => setUploadOpen(false)}
          onUploaded={async () => {
            await qc.invalidateQueries({ queryKey: ["emision-docs", solicitudId] });
            await qc.invalidateQueries({ queryKey: ["carta-aduana-emision-docs", solicitudId] });
          }}
        />
      )}

      {previewDocId && (
        <DocumentoPreviewModal documentoId={previewDocId} onClose={() => setPreviewDocId(null)} />
      )}
    </div>
  );
}
